/**
 * Paso Audiencia del asistente: el bloque «Por compras» (28/10/2026), debajo de lo que se eligió en la pestaña del
 * Público de Activa o de un público de la Base. De esa gente, sólo los que cumplen lo de sus compras en la base de
 * ventas EN USO: cuándo compraron por última vez, cuántas veces, cuánto gastan en promedio y en qué sucursal, y qué
 * pasa con quien no está en la base (por defecto, queda). Las reglas son del backend (`util/filtro-compras.util.ts`).
 *
 * - Plegado por defecto. Con un filtro puesto, la cabecera dice cuál, y abre solo si la campaña ya lo tenía.
 * - Debajo, cuántos quedan: lo dice el conteo de la pestaña (`resumen`), el mismo que después da el chequeo.
 * - Sin base de ventas en uso, lo dice («Subí la base de ventas…», con el link para el equipo) y no ofrece los campos:
 *   el asistente no deja guardar un filtro que no se puede aplicar.
 */

import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, Loader2, ShoppingBag, UploadCloud, X } from "lucide-react";
import type { FiltroCompras, OpcionesCompras, ResumenCompras } from "../types";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { R_SUBIR_BASE } from "../../base-ventas/logic/base";
import {
  FILTRO_COMPRAS_VACIO,
  TEXTO_SIN_BASE_DE_VENTAS,
  TEXTO_SIN_BASE_DE_VENTAS_COMERCIO,
  describirFiltroCompras,
  errorFiltroCompras,
  filtroComprasActivo,
  numeroDelCampo,
  textoHastaDeLaBase,
  textoQuedan,
} from "../logic/compras";

type CampoNumero = "ultimaHaceMasDe" | "ultimaHaceMenosDe" | "comprasMin" | "comprasMax" | "ticketMin" | "ticketMax";

interface Props {
  filtro: FiltroCompras;
  onFiltro: (f: FiltroCompras) => void;
  /** la base de ventas en uso (`GET /audiencias/compras`): null mientras llega */
  opciones: OpcionesCompras | null;
  errorOpciones?: string;
  onReintentar?: () => void;
  /** lo que dijo el conteo de la pestaña con el filtro: null sin filtro (o sin conteo todavía) */
  resumen: ResumenCompras | null;
  contando: boolean;
}

const INPUT = "w-24 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500";

export function FiltroComprasBloque({ filtro, onFiltro, opciones, errorOpciones = "", onReintentar, resumen, contando }: Props) {
  const equipo = useEsEquipoDeenex();
  const idPanel = useId();
  const nombreSinBase = useId();
  const activo = filtroComprasActivo(filtro);
  // Plegado, salvo que la campaña venga con un filtro (así se ve qué tiene antes de seguir). Hasta que se lo toca
  // (null) sigue al filtro: la campaña puede llegar después de que el bloque ya se mostró.
  const [abiertoElegido, setAbierto] = useState<boolean | null>(null);
  const abierto = abiertoElegido ?? activo;
  const cambiar = (f: FiltroCompras) => {
    if (abiertoElegido === null) setAbierto(true);
    onFiltro(f);
  };
  const error = activo ? errorFiltroCompras(filtro) : "";
  const condiciones = describirFiltroCompras(filtro);
  const base = opciones?.base ?? null;

  const numero = (clave: CampoNumero, etiqueta: string, entero = true) => (
    <input
      type="number"
      min={0}
      step={entero ? 1 : "any"}
      inputMode={entero ? "numeric" : "decimal"}
      aria-label={etiqueta}
      value={filtro[clave] ?? ""}
      onChange={(e) => cambiar({ ...filtro, [clave]: numeroDelCampo(e.target.value, entero) })}
      className={INPUT}
    />
  );
  const cambiarSucursal = (s: string, marcada: boolean) =>
    cambiar({ ...filtro, sucursales: marcada ? [...filtro.sucursales.filter((x) => x !== s), s] : filtro.sucursales.filter((x) => x !== s) });
  const limpiar = () => cambiar({ ...FILTRO_COMPRAS_VACIO });

  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="filtro-compras">
      <button type="button" aria-expanded={abierto} aria-controls={idPanel} onClick={() => setAbierto(!abierto)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex flex-wrap items-center gap-2">
          <ShoppingBag size={16} className="text-violet-600" />
          <span className="text-sm font-semibold text-slate-900">Por compras</span>
          {activo
            ? <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700">Filtrando</span>
            : <span className="text-xs text-slate-500">opcional: según lo que compraron en la base de ventas</span>}
        </span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${abierto ? "rotate-180" : ""}`} />
      </button>
      {!abierto && activo && (
        <p className="px-4 pb-3 text-xs text-slate-600" data-testid="filtro-compras-resumen">{condiciones.join(" · ")}</p>
      )}

      {abierto && (
        <div id={idPanel} className="space-y-4 border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
          {!opciones ? (
            errorOpciones ? (
              <p className="flex items-center justify-between gap-3 text-red-700" role="alert">
                <span>No pudimos traer la base de ventas. {errorOpciones}</span>
                {onReintentar && <button type="button" onClick={onReintentar} className="font-semibold underline">Reintentar</button>}
              </p>
            ) : (
              <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Buscando la base de ventas…</p>
            )
          ) : !base ? (
            <div className="space-y-2 rounded-lg bg-amber-50 px-3 py-2 text-amber-900" role="status" data-testid="filtro-compras-sin-base">
              <p className="flex items-start gap-2">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>{equipo ? TEXTO_SIN_BASE_DE_VENTAS : TEXTO_SIN_BASE_DE_VENTAS_COMERCIO}</span>
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {equipo && (
                  <Link to={R_SUBIR_BASE} className="inline-flex items-center gap-1.5 font-semibold text-violet-700 hover:underline">
                    <UploadCloud size={14} /> Subir la base de ventas
                  </Link>
                )}
                {/* una campaña que ya tenía el filtro, y la base dejó de estar en uso: no se puede guardar así */}
                {activo && (
                  <button type="button" onClick={limpiar} className="inline-flex items-center gap-1 font-semibold text-amber-900 underline">
                    <X size={13} /> Sacar el filtro
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="flex flex-wrap items-center gap-2">
                Compró por última vez hace más de {numero("ultimaHaceMasDe", "Compró por última vez hace más de (días)")} días
                <span>y menos de</span> {numero("ultimaHaceMenosDe", "Compró por última vez hace menos de (días)")} días
              </p>
              <p className="flex flex-wrap items-center gap-2">
                Compró al menos {numero("comprasMin", "Compró al menos (veces)")} veces
                <span>y como mucho</span> {numero("comprasMax", "Compró como mucho (veces)")} veces
              </p>
              <p className="flex flex-wrap items-center gap-2">
                Gasta en promedio entre $ {numero("ticketMin", "Gasta en promedio desde (pesos)", false)}
                <span>y $</span> {numero("ticketMax", "Gasta en promedio hasta (pesos)", false)}
              </p>
              <fieldset>
                <legend className="text-xs font-semibold text-slate-600 mb-1">Sucursal</legend>
                {base.sucursales.length ? (
                  <div className="flex max-h-40 flex-wrap gap-x-4 gap-y-1.5 overflow-y-auto">
                    {base.sucursales.map((s) => (
                      <label key={s} className="inline-flex items-center gap-1.5">
                        <input type="checkbox" className="accent-violet-600" checked={filtro.sucursales.includes(s)} onChange={(e) => cambiarSucursal(s, e.target.checked)} />
                        {s}
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">La base de ventas no tiene sucursales cargadas.</p>
                )}
                {filtro.sucursales.length === 0 && base.sucursales.length > 0 && <p className="mt-1 text-xs text-slate-500">Sin marcar ninguna, cualquier sucursal.</p>}
              </fieldset>
              <fieldset>
                <legend className="text-xs font-semibold text-slate-600 mb-1">Si no está en la base de ventas</legend>
                <div className="flex flex-wrap gap-4">
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" name={nombreSinBase} className="accent-violet-600" checked={filtro.sinBase === "dejar"} onChange={() => cambiar({ ...filtro, sinBase: "dejar" })} />
                    Dejarlo
                  </label>
                  <label className="inline-flex items-center gap-1.5">
                    <input type="radio" name={nombreSinBase} className="accent-violet-600" checked={filtro.sinBase === "sacar"} onChange={() => cambiar({ ...filtro, sinBase: "sacar" })} />
                    Sacarlo
                  </label>
                </div>
              </fieldset>
              {textoHastaDeLaBase(base) && <p className="text-xs text-slate-500">{textoHastaDeLaBase(base)}</p>}
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-red-700" role="alert">{error}</p>}
            </>
          )}

          {base && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2" aria-live="polite" data-testid="filtro-compras-quedan">
              {!activo ? (
                <p className="text-slate-500">Sin filtro: entran todos, hayan comprado o no.</p>
              ) : error ? (
                <p className="text-slate-500">Revisá el filtro para ver cuántos quedan.</p>
              ) : contando || !resumen ? (
                <p className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Contando…</p>
              ) : (
                <p><strong>{textoQuedan(resumen)}</strong></p>
              )}
              {activo && (
                <button type="button" onClick={limpiar} className="shrink-0 text-xs font-semibold text-violet-700 hover:underline">
                  Limpiar el filtro
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default FiltroComprasBloque;
